import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NbDateTimePickerComponent, NbDialogRef, NbDialogService } from '@nebular/theme';
import dayjs from 'dayjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { AnalyticsService } from '../../@core/utils';
import { dateTimeValidator } from '../../@theme/directives/validators';
import { ADMIN_EVENT_USER, UPCOMING_EVENTS } from '../../config/mock-data';
import { UserData } from '../../interface/user';
import { AuthService } from '../../services/auth.service';
import { CalendarService } from '../../services/calendar.service';
import { ClassroomService } from '../../services/classroom.service';
import { NotificationsService } from '../../services/notifications.service';
import { StorageService } from '../../services/storage.service';
import { ToasterService } from '../../services/toaster.service';
import { MatDialogComponent } from '../mat-dialog/mat-dialog.component';

@Component({
  selector: 'ngx-admin-event-forms',
  templateUrl: './admin-event-forms.component.html',
  styleUrls: ['./admin-event-forms.component.scss'],
})
export class AdminEventFormsComponent implements OnInit, AfterViewInit {
  addEventsForm: FormGroup;
  @Input() values;
  @Input() expand: boolean = false;
  loading = false;
  @Output() submitForm = new EventEmitter<boolean>();
  viewDate = new Date();
  startDate: Date;
  minStartDate: any;
  endDate: Date;
  minEndDate: Date;
  isLoading: false;
  private dialogRef: NbDialogRef<any>;
  mockData = this.storageservice.getItem('user').user['is_trial_user'] ? true : false;
  user: UserData;
  @Input() isTeacher: boolean;
  @ViewChild('dateTimePickerStart')
  dateTimePickerStart: NbDateTimePickerComponent<Date>;
  @ViewChild('dateTimePickerEnd')
  dateTimePickerEnd: NbDateTimePickerComponent<Date>;
  isView = false;
  @Input() schoolEndDate: Date;
  originalEventData: any;

  constructor(
    private fb: FormBuilder,
    public analyticsService: AnalyticsService,
    private calendarService: CalendarService,
    private authService: AuthService,
    private notificationService: NotificationsService,
    public dialog: MatDialog,
    private dialogService: NbDialogService,
    public storageservice: StorageService,
    private toasterService: ToasterService,
    private classroomService: ClassroomService,
    private spinnerService: NgxSpinnerService,
  ) {
    this.getFormControls();
  }

  async ngOnInit() {
    this.user = await this.storageservice.getItem('user');
    if (this.values?.adminEvent && this.values?.adminEvent?.user_id !== this.values?.user?.user?.id) {
      this.isView = true;
    }
    this.originalEventData = {
      title: this.values?.adminEvent?.title,
      start: new Date(this.values?.adminEvent?.start),
      end: new Date(this.values?.adminEvent?.end),
      viewUsers: this.values?.adminEvent?.view_users,
    };

    if (this.values?.isEditEvent) {
      this.addEventsForm.patchValue({
        title: this.values?.adminEvent?.title,
        start: new Date(this.values?.adminEvent?.start),
        end: new Date(this.values?.adminEvent?.end),
        viewUsers: this.values?.adminEvent?.view_users,
        filterUsers: '',
      });
    } else {
      this.addEventsForm.patchValue({
        filterUsers: '',
      });
    }

    if (this.mockData) {
      this.values.users = ADMIN_EVENT_USER;
    }
    const now = new Date();
    now.setMinutes(now.getMinutes() + 30); // Add 30 minutes to the current time
    now.setSeconds(0);
    now.setMilliseconds(0);
    const newStartDate = dayjs();
    this.minStartDate = newStartDate;
    if (this.isTeacher) this.getUsersForTeacher();
  }

  ngAfterViewInit() {
    const adjustStartDate = (startDate: Date) => {
      if (!this.values?.isEditEvent) {
        startDate?.setMinutes(startDate?.getMinutes() + 30);
      }
      this.adjustMinEndDate(startDate);
    };
    this.addEventsForm.get('start').valueChanges.subscribe((startDate: Date) => {
      adjustStartDate(startDate);
    });
  }

  adjustMinEndDate(startDate: Date): void {
    this.minEndDate = new Date(startDate);
    this.minEndDate.setHours(0, 0, 0, 0);
    this.dateTimePickerEnd.min =
      this.addEventsForm.value.start && this.values?.isEditEvent
        ? this.endDate
          ? this.endDate
          : this.minEndDate
        : this.addEventsForm?.value?.start;
  }

  getFormControls(): void {
    try {
      this.addEventsForm = this.fb.group({
        title: ['', Validators.required],
        start: ['', Validators.required],
        end: ['', Validators.required],
        viewUsers: [[]],
        filterUsers: [''],
      });
      this.timeValidation();
    } catch (error) {
      console.error('Error initializing form:', error);
    }
  }

  /**
   * @description Adds reactive validation to ensure the "end" time is at least 30 minutes after the "start" time.
   * The validation dynamically updates the `dateTimeValidator` for the "end" field to prevent selection of an end time before the start time.
   *
   * @returns {void} This method does not return any value. It sets the validators and subscribes to changes in the form controls.
   */

  timeValidation(): void {
    try {
      const startControl = this.addEventsForm.get('start');
      const endControl = this.addEventsForm.get('end');

      if (startControl && endControl) {
        const updateEndValidator = (startDate: Date) => {
          if (!startDate) return;

          const adjustedStart = new Date(startDate);
          adjustedStart.setMinutes(adjustedStart.getMinutes() - 30);

          endControl.setValidators([
            Validators.required,
            dateTimeValidator(new FormControl(adjustedStart), 'datetime'),
          ]);
          endControl.updateValueAndValidity({ emitEvent: false });
        };

        startControl.valueChanges.subscribe((startDate: Date) => {
          updateEndValidator(startDate);
        });

        endControl.valueChanges.subscribe(() => {
          const startDate = startControl.value;
          if (startDate) updateEndValidator(startDate);
        });
      }
    } catch (error) {
      console.log('Time validation error:', error);
    }
  }

  async addEvent() {
    try {
      if (this.addEventsForm.invalid) {
        this.addEventsForm.markAllAsTouched();
        this.loading = false;
        return;
      }

      const formValues = this.addEventsForm.value;
      const hasChanges =
        formValues.title !== this.originalEventData?.title ||
        formValues.start.getTime() !== this.originalEventData?.start.getTime() ||
        formValues.end.getTime() !== this.originalEventData?.end.getTime() ||
        !this.arraysEqual(formValues.viewUsers, this.originalEventData?.viewUsers);

      if (!hasChanges) {
        this.submitForm.emit(true);
        return;
      }
      this.spinnerService.show();
      this.analyticsService?.trackEvent('Add Admin School Events To Database', 'Admin School Events');

      const currentViewer = this.addEventsForm.value.viewUsers;
      const indexOfSelectAll = currentViewer.indexOf('SelectAll');
      if (indexOfSelectAll !== -1) {
        currentViewer.splice(indexOfSelectAll, 1);
      }
      const selectedStartDate = new Date(this.addEventsForm.value.start);
      if (this.values.isEditEvent) {
        selectedStartDate.setMinutes(selectedStartDate.getMinutes());
      } else {
        selectedStartDate.setMinutes(selectedStartDate.getMinutes() - 30);
      }
      const selectedSchoolByAdmin = this.storageservice.getItem('selectedSchoolByAdmin');
      const body = {
        start: selectedStartDate.getTime(),
        end: new Date(this.addEventsForm.value.end).getTime(),
        view_users: currentViewer,
        title: this.addEventsForm.value.title,
        school_id: selectedSchoolByAdmin ? selectedSchoolByAdmin.id : this.values.user.user.school_id,
        user_id: this.values.user.user.id,
        classroom_id: 0,
      };
      if (this.values?.isEditEvent) {
        body['id'] = this.values.adminEvent.id;
      }

      if (this.values.isEditEvent) {
        if (this.values.adminEvent) {
          const indexToUpdate = UPCOMING_EVENTS.findIndex((event) => event.id === body['id']);
          if (indexToUpdate !== -1) {
            UPCOMING_EVENTS[indexToUpdate] = body;
          } else {
            await this.calendarService.updateAdminEvent(this.values?.adminEvent.id, body);
          }
        }
      } else {
        await this.calendarService.addAdminSchoolEvents(body);
      }

      this.submitForm.emit(true);

      // Do not send notification to the users if trial user has added an event
      !this.values?.isEditEvent && !this.mockData ? await this.addNotification() : null;
      this.spinnerService.hide();
    } catch (error) {
      // console.log(error);
    }
  }

  async addNotification() {
    try {
      const notificationsToStore = {
        notifications: [],
      };
      const user: any = await this.authService.userById(this.values?.user.user.id);
      for (const iterator of this.addEventsForm.value.viewUsers) {
        const body = {
          title: 'You have new event for upcoming days',
          message: `${user.firstName + ' ' + user.lastName} added new event for you`,
          table_reference: 'admin_school_events',
          read_status: 'false',
          user_id: this.values.user.user.id,
          recieved_to: iterator,
        };
        notificationsToStore.notifications.push(body);
      }
      await this.notificationService.addMultipleNotifications(notificationsToStore);
    } catch (error) {
      // console.log(error);
    }
  }

  selectAllUsers() {
    try {
      const viewUsersForm = this.addEventsForm.get('viewUsers') as FormControl;
      const isSelectAllSelected = viewUsersForm.value.includes('SelectAll');
      if (isSelectAllSelected) {
        const allUserIds: any = this.values.users.map((user) => user.id);
        allUserIds.push('SelectAll');
        viewUsersForm.setValue(allUserIds);
      } else {
        this.addEventsForm.patchValue({
          viewUsers: [],
        });
      }
    } catch (error) {
      // console.log(error);
    }
  }

  selectUsers() {
    try {
      const viewUsersForm = this.addEventsForm.get('viewUsers') as FormControl;
      const selectedValues = viewUsersForm.value;
      const allUserIds = this.values.users.map((user) => user.id);
      const isContained = this.isArrayContained(allUserIds, selectedValues);
      if (isContained) {
        viewUsersForm.setValue(['SelectAll', ...allUserIds]);
      } else {
        const indexOfSelectAll = selectedValues.indexOf('SelectAll');
        if (indexOfSelectAll !== -1) {
          selectedValues.splice(indexOfSelectAll, 1);
        }
        this.addEventsForm.patchValue({
          viewUsers: [...selectedValues],
        });
      }
    } catch (error) {
      // console.error(error);
    }
  }

  isArrayContained(array1, array2) {
    try {
      return array1.every((element) => array2.includes(element));
    } catch (error) {}
  }

  async filterUsersByRole(event) {
    this.isTeacher ? await this.getUsersForTeacher() : await this.getUsersByRole();
    if (this.mockData) {
      this.values.users = ADMIN_EVENT_USER;
      this.toasterService.showToast('From here you can apply filter.', 'success');
      return;
    }
    if (event.target.value !== 'All') {
      this.values.users = this.values.users.filter((x) => x.role_id === Number(event.target.value));
    }
  }

  async getUsersByRole() {
    try {
      const body = {
        school_id: this.values.user.user.school_id,
      };

      this.values.users = await this.authService.userByRoleId(body);
      this.values?.users.splice(
        this.values.users.findIndex((user) => user.id === this.values.user.user.id),
        1,
      );
    } catch (error) {
      // console.log(error);
    }
  }

  async deleteEvent(dialog?) {
    try {
      this.analyticsService?.trackEvent('Delete Admin School Events From Database', 'Admin School Events');
      if (dialog) {
        // this.dialogRef = this.dialogService.open(dialog);
        this.openConfirDeletemDialog();
      } else {
        // this.dialogRef.close();
        this.mockData
          ? this.toasterService.showToast('You can delete events from here.', 'info')
          : await this.calendarService.deleteAdminEvent(this.values.adminEvent.id);
        this.submitForm.emit(true);
      }
    } catch (error) {}
  }

  closeDialog() {
    this.submitForm.emit(false);
  }

  async getClassroomByUserId() {
    try {
      const params = {
        user_id: this.user?.user.id,
        is_dashboard: true,
      };
      return await this.classroomService.getClassroomByUserId(params);
    } catch (error) {
      console.log(error);
    }
  }
  async getUsersForTeacher() {
    const data = await this.getClassroomByUserId();
    const idsArray = data.map((item: any) => item.code);
    const body = {
      classroom_ids: idsArray,
      school_id: Number(this.user?.user?.school_id),
    };
    try {
      const response = await this.calendarService.schoolEventBySchoolIdAndClassroomId(body);
      if (response.parents.length > 0 || response.students.length > 0) {
        const parentsAndStudents = [];
        response.parents.forEach((parent) => {
          parentsAndStudents.push(parent);
        });
        response.students.forEach((student) => {
          parentsAndStudents.push(student);
        });
        this.values.users = parentsAndStudents;
      }
    } catch (error) {
      // console.log(error);
    }
  }

  public openConfirDeletemDialog(): void {
    const dialogRef = this.dialog.open(MatDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Event',
        icon: '/assets/icons/featured icon.svg',
        accessCodeLabel: 'Delete Event',
        confirmationMessage: 'Are you sure want to delete this event ?',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Delete',
        cancelTrackEvent: 'Cancel Delete Event',
        confirmTrackEvent: 'Delete Event',
        trackPage: 'Event',
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.deleteEvent();
      }
    });
  }
  arraysEqual(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }
    return true;
  }
}
